import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import classnames from 'classnames';
import lodashCompact from 'lodash/fp/compact';
import moment from 'moment';

import {
  UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_MISSING_PROVIDER,
  UTILITY_PROVIDER_OTHER_ID,
  UTILITY_PROVIDER_TYPE_LINK,
  UTILITY_PROVIDER_TYPE_PENDING_SETUP,
  UTILITY_PARTNER_GROUP_ID_RELIANT,
  UTILITY_PROVIDER_ID_RELIANT,
} from 'common/utility/constants';

import {
  SR_API_ACTION_UTILITY_PROVIDER_GET_BY_AUTOCOMPLETE,
  SR_API_ACTION_UTILITY_PROVIDER_MARK_AS_TENANT_REMOVED,
} from 'api/utilityActionTypes';

import capitalize from 'common/util/capitalize';
import scrollToDomClass from 'common/util/scrollToDomClass';
import isUserAgentMobileApp from 'common/util/isUserAgentMobileApp';

import sendDataToMobileApp, {
  SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
} from 'client/helpers/sendDataToMobileApp';
import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityActivationActions from 'client/actions/UtilityActivation';

import {
  scrollToFormField,
  createInitialValues,
  createFormValidationFn,
  showFieldIf,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_ORANGE,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import SrUtilityProfitOptionCard from 'client/components/SrUtilityProfitOptionCard/SrUtilityProfitOptionCard';
import SrUtilitySetupContactProviderModal from 'client/components/SrUtilitySetup/SrUtilitySetupContactProviderModal';
import SrUtilitySetupUtilityNotNeededModal from 'client/components/SrUtilitySetup/SrUtilitySetupUtilityNotNeededModal';
import SrUtilitySetupDownloadUtilityDocs from 'client/components/SrUtilitySetup/SrUtilitySetupDownloadUtilityDocs/SrUtilitySetupDownloadUtilityDocs';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import ButtonDeprecated, { BUTTON_THEME_GREEN_ADOBE } from 'client/components/ButtonDeprecated';

import './SrUtilitySetupProvider.less';

const queryStringVariables = ['zip_code', 'first_name', 'last_name', 'email', 'phone_number'];

const makeUtilityProviderUrl = ({ provider, user, address }) => {
  if (!provider.urlQueryString) {
    return provider.url;
  }

  const requiredQueryStringVariables = queryStringVariables.filter((variable) => {
    return provider.urlQueryString.includes(`{${variable}}`);
  });

  const url = new URL(provider.url);
  const queryParams = {
    zip_code: address.zip,
    first_name: user?.firstName,
    last_name: user?.lastName,
    email: user?.email,
    phone_number: user?.phone,
  };

  const missingParams = requiredQueryStringVariables.filter((key) => !queryParams[key]);
  if (missingParams.length > 0) {
    return provider.url;
  }

  url.searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });

  return `${url.origin}${url.pathname}?${new URLSearchParams(queryParams).toString()}`;
};

const getFormFields = ({
  utilityActivation,
  utilityActivationItem,
  setUtilityProviderMatches,
  onFindUtilityProviders,
  formValues,
  hasAccountId,
  setHasAccountId,
} = {}) => {
  const missingProvider =
    utilityActivationItem.status === UTILITY_ACTIVATION_ITEM_STATUS_MISSING_PROVIDER;
  const shouldShowProofOfPurchase = utilityActivationItem.requiresProofOfPurchase || !hasAccountId;
  let leaseStartDateWarning = false;

  if (formValues.activationDate) {
    const activationDateFormat = moment(formValues.activationDate).format('YYYY-MM-DD');
    leaseStartDateWarning =
      new Date(activationDateFormat) > new Date(utilityActivation.leaseStartDate);
  }

  return [
    {
      id: 'utilityActivationItemId',
      type: 'hidden',
    },
    hideFieldIf(missingProvider, {
      id: 'utilityProviderId',
      title: 'Select the Provider you chose:',
      type: 'select',
      properties: {
        placeholder: 'Not selected',
        choices: utilityActivationItem.availableProviders
          .filter(({ isTenantRemoved }) => !isTenantRemoved)
          .reduce(
            (acc, provider) => {
              acc.unshift({
                value: provider.id,
                label: provider.title,
              });
              return acc;
            },
            [
              {
                value: UTILITY_PROVIDER_OTHER_ID,
                label: 'Other',
              },
            ],
          ),
      },
      validations: ['required'],
    }),
    showFieldIf(formValues.utilityProviderId === UTILITY_PROVIDER_OTHER_ID || missingProvider, {
      id: 'otherProviderName',
      title: 'Enter the name of the provider',
      type: 'autocomplete',
      properties: {
        placeholder: 'type company name here...',
        choices: [],
        onSuggestionsFetchRequested: ({ value }) => {
          return onFindUtilityProviders({ searchInput: value });
        },
        onBlur: () => {
          setTimeout(() => {
            setUtilityProviderMatches([]);
          }, 200);
        },
        filterFunc: () => {
          return true;
        },
      },
      validations: ['required'],
    }),
    {
      id: 'accountNumber',
      title: 'Account ID',
      titleClassName: !hasAccountId ? 'inactive' : null,
      titleToggle: {
        text: hasAccountId ? 'No ID available?' : 'Have the ID?',
        onClick: () => {
          setHasAccountId(!hasAccountId);
        },
      },
      type: 'short_text',
      properties: {
        placeholder: hasAccountId ? 'enter your utility account ID...' : 'no account ID available',
        disabled: !hasAccountId,
        style: !hasAccountId ? { color: '#D4D6D8', opacity: 0.5 } : {},
      },
      validations: hasAccountId ? ['utilityAccountIdValidator'] : [],
    },
    {
      id: 'activationDate',
      title: 'Activation date',
      type: 'date_picker',
      properties: {
        placeholder: 'What date did you set for the activation?',
        widget: leaseStartDateWarning ? (
          <SrBox margin="-4px 0 12px">
            <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_ORANGE} fontFamily="Whitney">
              You are activating this utility after your lease starts. Most landlords want utilities
              activated by the lease start date. Please confirm with your landlord that this is ok.
            </SrErrorMessage>
          </SrBox>
        ) : null,
      },
      validations: ['required'],
    },
    showFieldIf(shouldShowProofOfPurchase, {
      id: 'proofOfPurchaseUrl',
      title: 'Upload proof of purchase',
      type: 'file_single',
      properties: {
        fileAccept: '*',
        filePath:
          `utility-activations/${utilityActivationItem.utilityActivationId}` +
          `/item-${utilityActivationItem.id}/proof-of-purchase-{uuid}`,
      },
      validations: ['required'],
    }),
  ];
};

const SrUtilitySetupProvider = ({
  addressId,
  partnerName,
  partnerGroupId,
  utilityActivation,
  utilityActivationItem,
  utilityActivationShortId,
  userAgent,
  requiredDocs = [],
  isLoading,
  onSubmit,
  onHideUtility,
  onDownloadRequiredDocs,
}) => {
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [utilityProviderMatches, setUtilityProviderMatches] = useState([]);
  const [providerNotNeeded, setProviderNotNeeded] = useState(false);
  const [highlightForm, setHighlightForm] = useState(false);
  const [hasAccountId, setHasAccountId] = useState(
    utilityActivationItem.accountNumber || !utilityActivationItem.proofOfPurchaseUrl,
  );
  const missingProvider =
    utilityActivationItem.status === UTILITY_ACTIVATION_ITEM_STATUS_MISSING_PROVIDER;
  const dispatch = useDispatch();

  const getItemSubtitle = (utilityActivationItem) => {
    const utilityTypeLabel = utilityActivationItem?.type?.label
      ? utilityActivationItem.type.label.toLowerCase()
      : 'this utility';

    return `Click to sign up & turn on ${utilityTypeLabel}`;
  };

  const onOtherProviderSelect = (formikBag) => {
    formikBag.setFieldValue('utilityProviderId', UTILITY_PROVIDER_OTHER_ID);
    scrollToFormField({ formFieldId: 'otherProviderName' });

    if (missingProvider) {
      const isMobileApp = isUserAgentMobileApp(userAgent);
      const qs = `${utilityActivation.address.zip} ${utilityActivationItem.type.label} providers`;
      const link = `https://www.google.com/search?q=${encodeURIComponent(qs)}`;

      if (isMobileApp) {
        sendDataToMobileApp({
          actionType: SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
          url: link,
        });
      } else {
        global.open(link, '_blank');
      }
    }
  };

  const onMarkAsTenantRemoved = (formikBag) => {
    const isRemoved = !selectedProvider.isTenantRemoved;

    dispatch(requestUtilityApi, {
      queryId: selectedProvider.id,
      action: SR_API_ACTION_UTILITY_PROVIDER_MARK_AS_TENANT_REMOVED,
      payload: {
        addressId: addressId,
        utilityProviderId: selectedProvider.id,
        isRemoved,
      },
    })
      .then(() => {
        return dispatch(UtilityActivationActions.getByShortId, {
          utilityActivationShortId,
          forceRefresh: true,
        });
      })
      .then(() => {
        setSelectedProvider(null);
        if (isRemoved && formikBag.values.utilityProviderId === String(selectedProvider.id)) {
          formikBag.setFieldValue('utilityProviderId', UTILITY_PROVIDER_OTHER_ID);
        }
      });
  };

  const onFindUtilityProviders = ({ searchInput }) => {
    if (!searchInput) {
      setUtilityProviderMatches([]);
      return;
    }

    dispatch(requestUtilityApi, {
      queryId: searchInput,
      action: SR_API_ACTION_UTILITY_PROVIDER_GET_BY_AUTOCOMPLETE,
      payload: {
        searchInput,
        limit: 4,
        utilityTypeIds: lodashCompact([utilityActivationItem?.type?.id]),
        // TODO(@d-oliveros): Rename utilityProvider.type to utilityProvider.category
        types: [UTILITY_PROVIDER_TYPE_LINK, UTILITY_PROVIDER_TYPE_PENDING_SETUP],
      },
    }).then((autocompletePredictions) => {
      setUtilityProviderMatches(autocompletePredictions || []);
    });
  };

  const onHideUtilityClick = () => {
    setProviderNotNeeded(true);
    onHideUtility();
  };

  const handleSubmit = (values) => {
    if (!hasAccountId) {
      values.accountNumber = null;
    } else if (!utilityActivationItem.requiresProofOfPurchase) {
      values.proofOfPurchaseUrl = null;
    }

    return onSubmit(values);
  };

  const utilityActivationLabel = utilityActivationItem?.type?.label.toLowerCase();
  const defaultFormValues = {
    utilityActivationItemId: utilityActivationItem.id,
    utilityProviderId: utilityActivationItem.utilityProviderId,
    accountNumber: utilityActivationItem.accountNumber,
    activationDate: utilityActivationItem.activationDate,
    proofOfPurchaseUrl: utilityActivationItem.proofOfPurchaseUrl,
  };

  return (
    <div styleName="view">
      <div styleName="titleBox">
        <span>
          Turn on the {utilityActivationLabel}
          <SrBox
            backgroundImage={`url(${utilityActivationItem.type.iconUrl})`}
            backgroundSize="contain"
            backgroundPosition="center"
            backgroundRepeat="no-repeat"
            width="22px"
            height="22px"
            margin="0 2px"
            display="inlineBlock"
            transform="translateY(2px)"
          />
          <br />
          for your address.
        </span>
      </div>

      <Formik
        onSubmit={handleSubmit}
        initialValues={createInitialValues({
          formFields: getFormFields({
            utilityActivation,
            utilityActivationItem,
            formValues: defaultFormValues,
            hasAccountId,
            setHasAccountId,
          }),
          setUtilityProviderMatches,
          onFindUtilityProviders,
          defaultValues: defaultFormValues,
        })}
        validate={(values) => {
          const formFields = getFormFields({
            utilityActivation,
            utilityActivationItem,
            setUtilityProviderMatches,
            onFindUtilityProviders,
            formValues: values,
            hasAccountId,
            setHasAccountId,
          });
          return createFormValidationFn(formFields)(values);
        }}
        render={(formikBag) => {
          const formFields = getFormFields({
            utilityActivation,
            utilityActivationItem,
            setUtilityProviderMatches,
            onFindUtilityProviders,
            formValues: formikBag.values,
            hasAccountId,
            setHasAccountId,
          });

          const onClickProvider = ({ shouldOpenModal = true, provider }) => {
            formikBag.setFieldValue('utilityProviderId', provider.id);
            scrollToDomClass('step2');
            setSelectedProvider(null);

            if (shouldOpenModal) {
              setHighlightForm(true);
            }
            else {
              global.open(provider.url, '_blank');
            }

            const el = global.document.querySelector('[name="accountNumber"]');
            if (el) {
              el.focus();
            }
          };

          const getProviderItemProps = (provider) => {
            const props = {};

            const isReliantProvider =
              provider.id === UTILITY_PROVIDER_ID_RELIANT &&
              partnerGroupId === UTILITY_PARTNER_GROUP_ID_RELIANT;

            if (isReliantProvider) {
              props.onContainerClick = () => onClickProvider({ shouldOpenModal: false, provider });
            } else if (provider.phone) {
              props.onContainerClick = () => setSelectedProvider(provider);
            } else if (provider.url) {
              props.link = provider.url;
              props.target = '_blank';
            }

            return props;
          };

          return (
            <div styleName="contentBox">
              <div styleName="text">
                <span styleName="text strong">Step 1:</span>
              </div>
              <div styleName="text">Create account on their website.</div>
              <div styleName="availableProvidersBox">
                {requiredDocs.length > 0 && (
                  <SrUtilitySetupDownloadUtilityDocs
                    note={requiredDocs[0].noteToTenant}
                    filesCount={requiredDocs.length}
                    utilityPartnerName={partnerName}
                    onDownload={onDownloadRequiredDocs}
                  />
                )}
                {utilityActivationItem.availableProviders
                  .filter((provider) => provider.type !== UTILITY_PROVIDER_TYPE_PENDING_SETUP)
                  .map((provider) => {
                    const itemSubtitle = getItemSubtitle(utilityActivationItem);
                    const providerItemProps = getProviderItemProps(provider);
                    const note =
                      [provider.note, provider.listingNote].filter(Boolean).join('\n\n') || null;
                    return (
                      <SrUtilityProfitOptionCard
                        key={provider.id}
                        title={provider.title}
                        isTenantRemoved={provider.isTenantRemoved}
                        detailIconUrl={utilityActivationItem.type.iconUrl}
                        detailColor={utilityActivationItem.type.iconBackgroundColor}
                        subtitle={itemSubtitle}
                        showArrow
                        notes={
                          !note
                            ? null
                            : [
                              {
                                title: 'Note from Property Manager:',
                                value: note,
                              },
                            ]
                        }
                        {...providerItemProps}
                      />
                    );
                  })}
                <SrUtilityProfitOptionCard
                  title={
                    missingProvider
                      ? `${utilityActivation.address.city} ${capitalize(utilityActivationLabel)}`
                      : 'Other company'
                  }
                  detailIconUrl={
                    missingProvider
                      ? utilityActivationItem.type.iconUrl
                      : '/images/question-mark.svg'
                  }
                  detailColor={utilityActivationItem.type.iconBackgroundColor}
                  subtitle={
                    missingProvider
                      ? 'Click to find your provider on Google.'
                      : 'Click if company not listed above.'
                  }
                  onContainerClick={() => onOtherProviderSelect(formikBag)}
                />
                {utilityActivationItem.status !== UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED && (
                  <SrBox
                    marginTop="8px"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    cursor="pointer"
                    onClick={() => setProviderNotNeeded(true)}
                  >
                    <SrText
                      styleName="text"
                      font="greycliff"
                      fontSize="14px"
                      lineHeight="20px"
                      color="blue300"
                      weight="600"
                    >
                      Have you been told you don't need {utilityActivationLabel}?
                    </SrText>
                  </SrBox>
                )}
              </div>
              <div className="step2">
                <div styleName="text">
                  <span styleName="text strong">Step 2:</span>
                </div>
                <div styleName="text">
                  Once you create an account with a provider above,&nbsp; enter that account's ID
                  below to confirm you've turned it on.
                </div>
                <div styleName={classnames('formBox', { highlight: highlightForm })}>
                  <Form>
                    {formFields.map((formField) => (
                      <SrBox key={formField.id}>
                        <SrFormikField
                          formikBag={formikBag}
                          formField={formField}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        {formField.id === 'otherProviderName' &&
                          formikBag.values?.utilityProviderId === UTILITY_PROVIDER_OTHER_ID &&
                          utilityProviderMatches.length > 0 && (
                            <SrBox styleName="autocompleteBox">
                              {utilityProviderMatches.map((utilityProvider) => (
                                <SrBox
                                  styleName="autocompleteItem"
                                  key={`autocomplete_${utilityProvider.id}`}
                                  onClick={() => {
                                    formikBag.setFieldValue(
                                      'otherProviderName',
                                      utilityProvider.title,
                                    );
                                    setUtilityProviderMatches([]);
                                  }}
                                >
                                  <SrText styleName="autocompleteItemText">
                                    {utilityProvider.title}
                                  </SrText>
                                </SrBox>
                              ))}
                            </SrBox>
                          )}
                      </SrBox>
                    ))}
                    <ButtonDeprecated
                      margin="24px 0 0"
                      type="submit"
                      theme={BUTTON_THEME_GREEN_ADOBE}
                      disabled={isLoading}
                    >
                      Save
                    </ButtonDeprecated>
                  </Form>
                </div>
              </div>
              {selectedProvider && (
                <SrUtilitySetupContactProviderModal
                  iconURL={utilityActivationItem.type.iconUrl}
                  iconBgColor={utilityActivationItem.type.iconBackgroundColor}
                  providerURL={makeUtilityProviderUrl({
                    provider: selectedProvider,
                    user: utilityActivation.user,
                    address: utilityActivation.address,
                  })}
                  providerName={selectedProvider.title}
                  providerPhoneNumber={selectedProvider.phone}
                  isTenantRemoved={selectedProvider.isTenantRemoved}
                  onMarkAsTenantRemoved={() => onMarkAsTenantRemoved(formikBag)}
                  onClose={() => setSelectedProvider(null)}
                  onClickProvider={() => onClickProvider({ shouldOpenModal: true, provider: selectedProvider })}
                />
              )}
            </div>
          );
        }}
      />
      {providerNotNeeded && (
        <SrUtilitySetupUtilityNotNeededModal
          onHideUtility={onHideUtilityClick}
          onClose={() => setProviderNotNeeded(false)}
        />
      )}
    </div>
  );
};

SrUtilitySetupProvider.displayName = 'SrUtilitySetupProvider';

SrUtilitySetupProvider.propTypes = {
  addressId: PropTypes.number.isRequired,
  partnerName: PropTypes.string.isRequired,
  partnerGroupId: PropTypes.number,
  utilityActivationItem: PropTypes.object,
  requiredDocs: PropTypes.array,
  utilityActivationShortId: PropTypes.string,
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onHideUtility: PropTypes.func.isRequired,
  onDownloadRequiredDocs: PropTypes.func.isRequired,
};

export default SrUtilitySetupProvider;
